/**
 * InlineShareActions — compact like / comment / follow / share row for the page header.
 *
 * Renders: heart (like), comment bubble, follow pill, share button.
 * The share button opens the full Share dialog (same as sidebar).
 * Designed to sit in the DetailView header below the title + assignments row.
 */

import { useState, useRef, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Share2,
  Heart,
  MessageCircle,
  BadgeCheck,
  Copy,
  Check,
  X,
  Tag,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { UserAvatar } from "./UserAvatar";
import {
  useUpdateDocShare,
  useDeleteDocShare,
  useUpdatePublicShare,
} from "../api/useDocShare";
import { useAssignUserSuggestions } from "../api/useDocAssignment";
import { useToggleFollow } from "../api/useToggleFollow";
import { useToggleLike } from "../api/likeHooks";
import { useDebounce } from "@/lib/hooks/useDebounce";
import { useDetailPageShare } from "@/contexts/DetailPageShareContext";
import type { DocSidebarShare, DocSidebarPublicShare } from "@/lib/erp/docs";
import { useLanguage } from "@/i18n/useLanguage";
import { getUITranslation } from "@/lib/views/labels/translations";
import { useCurrentUser } from "@/lib/hooks/useCurrentUser";

interface InlineShareActionsProps {
  doctype: string;
  name: string;
  shares: DocSidebarShare[];
  public_share: DocSidebarPublicShare;
  share_capabilities?: {
    can_read: boolean;
    can_write: boolean;
    can_share: boolean;
    can_submit: boolean;
  };
  liked_by: string[];
  like_count: number;
  comment_count: number;
  followed: boolean;
  editable?: boolean;
  onComment?: () => void;
  /** Optional tag button rendered between comment and follow */
  tagCount?: number;
  onTagClick?: () => void;
}

export function InlineShareActions({
  doctype,
  name,
  shares,
  public_share,
  share_capabilities,
  liked_by,
  like_count,
  comment_count,
  followed,
  editable = false,
  onComment,
  tagCount,
  onTagClick,
}: InlineShareActionsProps) {
  const { language } = useLanguage();
  const { data: currentUser } = useCurrentUser();
  const userId = currentUser?.email || currentUser?.name;
  const { showShareDialog, setShowShareDialog } = useDetailPageShare();

  const [shareUserSearch, setShareUserSearch] = useState("");
  const [selectedShareUser, setSelectedShareUser] = useState<string | null>(null);
  const [showShareUserSuggestions, setShowShareUserSuggestions] = useState(false);
  const [sharePermissions, setSharePermissions] = useState({
    can_write: false,
    can_share: false,
    can_submit: false,
  });
  const [copiedUrl, setCopiedUrl] = useState(false);

  const shareUserSearchRef = useRef<HTMLInputElement>(null);
  const shareUserSuggestionsRef = useRef<HTMLDivElement>(null);

  const updateDocShareMutation = useUpdateDocShare(doctype, name);
  const deleteDocShareMutation = useDeleteDocShare(doctype, name);
  const updatePublicShareMutation = useUpdatePublicShare(doctype, name);

  const {
    followed: isFollowed,
    toggle: toggleFollow,
    isLoading: isTogglingFollow,
  } = useToggleFollow({ doctype, name, initialFollowed: followed });

  const {
    liked: isLiked,
    toggle: toggleLike,
    isLoading: isTogglingLike,
  } = useToggleLike({ doctype, name, initialLikedBy: liked_by });

  const debouncedShareUserSearch = useDebounce(shareUserSearch, 300);

  const { data: shareUserSuggestions = [], isLoading: isLoadingShareUsers } =
    useAssignUserSuggestions(debouncedShareUserSearch, {
      enabled: editable,
      isOpen: showShareUserSuggestions,
      minLength: 0,
    });

  const uiLabels = useMemo(() => {
    const getUI = (key: string, fallback: string) =>
      getUITranslation(key, language) || fallback;
    return {
      share: getUI("share", "Share"),
      everyone: getUI("everyone", "Everyone"),
      anyoneWithAccess: getUI("anyone_with_access", "Anyone with access"),
      canRead: getUI("can_read", "Can read"),
      canWrite: getUI("can_write", "Can write"),
      canShare: getUI("can_share", "Can share"),
      canSubmit: getUI("can_submit", "Can submit"),
      read: getUI("read", "Read"),
      write: getUI("write", "Write"),
      submit: getUI("submit", "Submit"),
      sharedBy: getUI("shared_by", "Shared by"),
      existingShares: getUI("existing_shares", "Existing shares"),
      notSharedYet: getUI("not_shared_yet", "Not shared with anyone yet."),
      addNewUser: getUI("add_new_user", "Add new user"),
      searchUsers: getUI("search_users", "Search users..."),
      loadingUsers: getUI("loading_users", "Loading users..."),
      noMatchingUsers: getUI("no_matching_users", "No matching users"),
      sharingInProgress: getUI("sharing_in_progress", "Sharing..."),
      copyLink: getUI("copy_link", "Copy link"),
      copied: getUI("copied", "Copied"),
      like: getUI("like", "Like"),
      unlike: getUI("unlike", "Unlike"),
      follow: getUI("follow", "Follow"),
      following: getUI("following", "Following"),
    };
  }, [language]);

  const handleCopyLink = () => {
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(window.location.href)
        .then(() => {
          setCopiedUrl(true);
          setTimeout(() => setCopiedUrl(false), 2000);
        })
        .catch(() => {});
    }
  };

  const handleUpdateShare = (
    share: DocSidebarShare,
    permissions: { can_write: boolean; can_share: boolean; can_submit: boolean }
  ) => {
    updateDocShareMutation.mutate({ user: share.user, can_read: true, ...permissions });
  };

  const handleDeleteShare = (user: string) => {
    deleteDocShareMutation.mutate({ user });
  };

  const handleAddShare = () => {
    if (selectedShareUser) {
      updateDocShareMutation.mutate(
        { user: selectedShareUser, can_read: true, ...sharePermissions },
        {
          onSuccess: () => {
            setSelectedShareUser(null);
            setShareUserSearch("");
            setSharePermissions({ can_write: false, can_share: false, can_submit: false });
          },
        }
      );
    }
  };

  useEffect(() => {
    if (!showShareDialog) {
      setShareUserSearch("");
      setSelectedShareUser(null);
      setSharePermissions({ can_write: false, can_share: false, can_submit: false });
      setShowShareUserSuggestions(false);
    }
  }, [showShareDialog]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        shareUserSuggestionsRef.current &&
        !shareUserSuggestionsRef.current.contains(event.target as Node) &&
        shareUserSearchRef.current &&
        !shareUserSearchRef.current.contains(event.target as Node)
      ) {
        setShowShareUserSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        {/* Like */}
        <button
          type="button"
          onClick={toggleLike}
          disabled={isTogglingLike || !userId}
          className="inline-flex items-center gap-1 hover:text-foreground transition-colors disabled:opacity-50"
          aria-label={isLiked ? uiLabels.unlike : uiLabels.like}
        >
          <Heart
            className={cn(
              "h-3.5 w-3.5",
              isLiked && "fill-destructive text-destructive"
            )}
          />
          <span className="tabular-nums">{like_count}</span>
          <span>{like_count === 1 ? uiLabels.like.toLowerCase() : `${uiLabels.like.toLowerCase()}s`}</span>
        </button>

        {/* Comment */}
        <button
          type="button"
          onClick={onComment}
          className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
        >
          <MessageCircle className="h-3.5 w-3.5" />
          <span className="tabular-nums">{comment_count}</span>
          <span>comments</span>
        </button>

        {/* Tag */}
        {tagCount != null && tagCount > 0 && (
          <button
            type="button"
            onClick={onTagClick}
            className="inline-flex items-center gap-1 hover:text-foreground transition-colors"
          >
            <Tag className="h-3.5 w-3.5" />
            <span className="tabular-nums">{tagCount}</span>
            <span>tags</span>
          </button>
        )}

        <div className="flex-1" />

        {/* Follow */}
        <button
          type="button"
          onClick={toggleFollow}
          disabled={isTogglingFollow}
          className={cn(
            "inline-flex items-center gap-1.5 transition-colors disabled:opacity-50",
            isFollowed
              ? "text-primary font-medium"
              : "hover:text-foreground"
          )}
        >
          {isFollowed && <BadgeCheck className="h-3.5 w-3.5" />}
          <span>{isFollowed ? uiLabels.following : uiLabels.follow}</span>
        </button>

        {/* Share */}
        <button
          type="button"
          onClick={() => setShowShareDialog(true)}
          className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors"
        >
          <Share2 className="h-3.5 w-3.5" />
          <span>{uiLabels.share}</span>
        </button>
      </div>

      {/* Share Dialog — same as sidebar version */}
      <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto p-0 flex flex-col">
          <DialogHeader className="px-10 pt-20 pb-4 !flex-row flex items-center justify-between space-y-0">
            <DialogTitle className="text-[22px] font-semibold text-foreground leading-tight tracking-[-0.2px]">
              {uiLabels.share}
            </DialogTitle>
            <Button
              onClick={handleCopyLink}
              className="h-10 px-5 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-medium text-sm"
              disabled={copiedUrl}
            >
              {copiedUrl ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  {uiLabels.copied}
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4 mr-2" />
                  {uiLabels.copyLink}
                </>
              )}
            </Button>
          </DialogHeader>

          <div className="pb-8 px-10 pt-2 space-y-6 overflow-y-auto flex-1">
            {/* Everyone / Anyone Section */}
            <div className="space-y-3">
              <h3 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                {uiLabels.everyone}
              </h3>
              <div className="flex flex-col gap-3 rounded-md border border-border/40 bg-muted p-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">
                    {uiLabels.anyoneWithAccess}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">{uiLabels.canRead}</span>
                    <Switch
                      checked={public_share.enabled && public_share.can_read}
                      onCheckedChange={(checked) =>
                        updatePublicShareMutation.mutate({
                          can_read: checked,
                          can_write: public_share.can_write,
                          can_share: public_share.can_share,
                          can_submit: public_share.can_submit,
                        })
                      }
                      disabled={updatePublicShareMutation.isPending || !share_capabilities?.can_read}
                    />
                  </div>
                </div>
                {public_share.enabled && public_share.can_read && (
                  <div className="flex flex-wrap gap-4 pt-2 border-t border-border/40">
                    {share_capabilities?.can_write && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{uiLabels.canWrite}</span>
                        <Switch
                          checked={public_share.can_write}
                          onCheckedChange={(checked) =>
                            updatePublicShareMutation.mutate({
                              can_read: true,
                              can_write: checked,
                              can_share: public_share.can_share,
                              can_submit: public_share.can_submit,
                            })
                          }
                          disabled={updatePublicShareMutation.isPending}
                        />
                      </div>
                    )}
                    {share_capabilities?.can_share && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{uiLabels.canShare}</span>
                        <Switch
                          checked={public_share.can_share}
                          onCheckedChange={(checked) =>
                            updatePublicShareMutation.mutate({
                              can_read: true,
                              can_write: public_share.can_write,
                              can_share: checked,
                              can_submit: public_share.can_submit,
                            })
                          }
                          disabled={updatePublicShareMutation.isPending}
                        />
                      </div>
                    )}
                    {share_capabilities?.can_submit && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">{uiLabels.canSubmit}</span>
                        <Switch
                          checked={public_share.can_submit}
                          onCheckedChange={(checked) =>
                            updatePublicShareMutation.mutate({
                              can_read: true,
                              can_write: public_share.can_write,
                              can_share: public_share.can_share,
                              can_submit: checked,
                            })
                          }
                          disabled={updatePublicShareMutation.isPending}
                        />
                      </div>
                    )}
                  </div>
                )}
                {public_share.enabled && public_share.shared_by && (
                  <div className="text-[11px] text-muted-foreground pt-1">
                    {uiLabels.sharedBy} {public_share.shared_by}
                    {public_share.shared_on && (
                      <> &middot; {new Date(public_share.shared_on).toLocaleString()}</>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Existing Shares */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium text-foreground">
                {uiLabels.existingShares} {shares.length > 0 && `(${shares.length})`}
              </h3>
              {shares.length === 0 ? (
                <p className="text-sm text-muted-foreground">{uiLabels.notSharedYet}</p>
              ) : (
                <div className="space-y-2">
                  {shares.map((share) => (
                    <div
                      key={share.user}
                      className="flex items-center justify-between p-4 rounded-md bg-muted border border-border/40"
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <UserAvatar
                          imageUrl={share.avatar}
                          fallbackText={share.full_name || share.user}
                          size="md"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {share.full_name || share.user}
                          </p>
                          {share.shared_by && (
                            <p className="text-xs text-muted-foreground">
                              {uiLabels.sharedBy} {share.shared_by}
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-4 ml-4">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{uiLabels.read}</span>
                            <Switch checked disabled className="opacity-60" />
                          </div>
                          {share_capabilities?.can_write && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{uiLabels.write}</span>
                              <Switch
                                checked={share.can_write}
                                onCheckedChange={(checked) =>
                                  handleUpdateShare(share, {
                                    can_write: checked,
                                    can_share: share.can_share,
                                    can_submit: share.can_submit,
                                  })
                                }
                                disabled={updateDocShareMutation.isPending}
                              />
                            </div>
                          )}
                          {share_capabilities?.can_share && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{uiLabels.canShare}</span>
                              <Switch
                                checked={share.can_share}
                                onCheckedChange={(checked) =>
                                  handleUpdateShare(share, {
                                    can_write: share.can_write,
                                    can_share: checked,
                                    can_submit: share.can_submit,
                                  })
                                }
                                disabled={updateDocShareMutation.isPending}
                              />
                            </div>
                          )}
                          {share_capabilities?.can_submit && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{uiLabels.submit}</span>
                              <Switch
                                checked={share.can_submit}
                                onCheckedChange={(checked) =>
                                  handleUpdateShare(share, {
                                    can_write: share.can_write,
                                    can_share: share.can_share,
                                    can_submit: checked,
                                  })
                                }
                                disabled={updateDocShareMutation.isPending}
                              />
                            </div>
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-full hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => handleDeleteShare(share.user)}
                          disabled={deleteDocShareMutation.isPending}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Add New User */}
            {editable && share_capabilities && (
              <div className="space-y-3">
                <h3 className="text-sm font-medium text-foreground">
                  {uiLabels.addNewUser}
                </h3>
                <div className="relative">
                  <Input
                    ref={shareUserSearchRef}
                    placeholder={uiLabels.searchUsers}
                    value={shareUserSearch}
                    onChange={(e) => {
                      setShareUserSearch(e.target.value);
                      if (!showShareUserSuggestions) setShowShareUserSuggestions(true);
                    }}
                    onFocus={() => setShowShareUserSuggestions(true)}
                    className="h-12 rounded-md bg-muted border-0 px-4 text-sm font-normal text-foreground"
                  />
                  {showShareUserSuggestions && (
                    <div
                      ref={shareUserSuggestionsRef}
                      className="absolute z-50 w-full mt-1 bg-background border border-border rounded-sm shadow-lg max-h-96 overflow-auto"
                    >
                      {isLoadingShareUsers && (
                        <div className="px-4 py-3 text-sm text-muted-foreground">
                          {uiLabels.loadingUsers}
                        </div>
                      )}
                      {!isLoadingShareUsers &&
                        shareUserSuggestions
                          .filter((s) => !shares.some((sh) => sh.user === s.value))
                          .slice(0, 10)
                          .map((suggestion) => (
                            <button
                              key={suggestion.value}
                              type="button"
                              onClick={() => {
                                setSelectedShareUser(suggestion.value);
                                setShareUserSearch(suggestion.label);
                                setShowShareUserSuggestions(false);
                              }}
                              className="w-full text-left px-5 py-4 md:py-3.5 text-base md:text-sm flex items-center gap-3 md:gap-2 touch-manipulation min-h-[56px] md:min-h-[52px]"
                            >
                              <UserAvatar
                                imageUrl={suggestion.avatar}
                                fallbackText={suggestion.label}
                                size="md"
                              />
                              <span className="text-foreground">
                                {suggestion.label}
                              </span>
                            </button>
                          ))}
                      {!isLoadingShareUsers &&
                        shareUserSuggestions.filter(
                          (s) => !shares.some((sh) => sh.user === s.value)
                        ).length === 0 && (
                          <div className="px-4 py-3 text-sm text-muted-foreground">
                            {uiLabels.noMatchingUsers}
                          </div>
                        )}
                    </div>
                  )}
                </div>
                {selectedShareUser && (
                  <>
                    <div className="space-y-3 pt-2">
                      {share_capabilities.can_write && (
                        <div className="flex items-center justify-between">
                          <label className="text-sm text-foreground">
                            {uiLabels.canWrite}
                          </label>
                          <Switch
                            checked={sharePermissions.can_write}
                            onCheckedChange={(checked) =>
                              setSharePermissions((prev) => ({ ...prev, can_write: checked }))
                            }
                          />
                        </div>
                      )}
                      {share_capabilities.can_share && (
                        <div className="flex items-center justify-between">
                          <label className="text-sm text-foreground">
                            {uiLabels.canShare}
                          </label>
                          <Switch
                            checked={sharePermissions.can_share}
                            onCheckedChange={(checked) =>
                              setSharePermissions((prev) => ({ ...prev, can_share: checked }))
                            }
                          />
                        </div>
                      )}
                      {share_capabilities.can_submit && (
                        <div className="flex items-center justify-between">
                          <label className="text-sm text-foreground">
                            {uiLabels.canSubmit}
                          </label>
                          <Switch
                            checked={sharePermissions.can_submit}
                            onCheckedChange={(checked) =>
                              setSharePermissions((prev) => ({ ...prev, can_submit: checked }))
                            }
                          />
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={handleAddShare}
                      disabled={updateDocShareMutation.isPending || !selectedShareUser}
                      className="w-full h-12 rounded-md bg-primary hover:bg-primary/90 text-primary-foreground font-medium"
                    >
                      {updateDocShareMutation.isPending
                        ? uiLabels.sharingInProgress
                        : uiLabels.share}
                    </Button>
                  </>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
